import React, { useRef, useState } from "react";
import { toast } from "sonner";
import {
  FileSpreadsheet, Loader2, CheckCircle2, Download, Link as LinkIcon, ArrowRight,
} from "lucide-react";
import { importApi } from "../../lib/api";
import { getApiError } from "../../lib/utils";
import { Stat } from "./Stat";

/** Bulk update of maps_link ONLY. Never touches any other facility field. */
export const MapsLinksTab = ({ onApplied }) => {
  const mapsRef = useRef(null);
  const [mapsFile, setMapsFile] = useState(null);
  const [mapsPreview, setMapsPreview] = useState(null);
  const [mapsPreviewing, setMapsPreviewing] = useState(false);
  const [mapsApplying, setMapsApplying] = useState(false);
  const [mapsReport, setMapsReport] = useState(null);

  const previewMaps = async () => {
    if (!mapsFile) return;
    setMapsPreviewing(true);
    setMapsPreview(null);
    setMapsReport(null);
    try {
      const r = await importApi.previewMapsLinks(mapsFile);
      setMapsPreview(r);
    } catch (e) {
      toast.error(getApiError(e, "Preview failed"));
    } finally { setMapsPreviewing(false); }
  };

  const applyMaps = async () => {
    if (!mapsFile) return;
    setMapsApplying(true);
    try {
      const r = await importApi.applyMapsLinks(mapsFile);
      setMapsReport(r);
      setMapsFile(null);
      setMapsPreview(null);
      toast.success(`Maps links updated (${r.updated}/${r.total_rows})`);
      onApplied?.();
    } catch (e) {
      toast.error(getApiError(e, "Update failed"));
    } finally { setMapsApplying(false); }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-nmi-200 bg-nmi-50 p-4 text-xs text-slate-800">
        <div className="flex items-start gap-2">
          <LinkIcon size={16} className="mt-0.5 shrink-0 text-nmi-700" />
          <div>
            <div className="font-heading text-sm font-bold text-nmi-900 mb-1">
              Bulk update Google Maps links only
            </div>
            <p>
              Upload an Excel sheet with columns <code>facility_id</code>, <code>name</code>, and
              <code> maps_link</code>. We match by <b>ID first</b>, then fall back to <b>name</b> (exact,
              case-insensitive). Rows with a blank <code>maps_link</code> are <b>skipped</b> — existing links
              are never cleared. No other facility field is touched.
            </p>
            <p className="mt-1">
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/import/facilities/maps-links-template.xlsx`}
                className="inline-flex items-center gap-1 text-nmi-800 underline hover:text-nmi-900"
              >
                <Download size={12} /> Download the maps-links template
              </a>
            </p>
          </div>
        </div>
      </div>

      <div
        data-testid="maps-links-dropzone"
        onClick={() => mapsRef.current?.click()}
        className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-nmi-300 bg-white p-10 text-center hover:bg-nmi-50/60"
      >
        <input
          data-testid="maps-links-file-input"
          ref={mapsRef}
          type="file"
          accept=".xlsx,.xlsm"
          className="hidden"
          onChange={(e) => {
            setMapsFile(e.target.files?.[0] || null);
            setMapsPreview(null);
            setMapsReport(null);
          }}
        />
        <div className="flex h-12 w-12 items-center justify-center rounded-md bg-nmi-50 border border-nmi-200">
          <LinkIcon size={22} className="text-nmi-700" strokeWidth={2} />
        </div>
        <div className="mt-3 font-heading text-lg font-bold text-slate-900">
          {mapsFile ? mapsFile.name : "Drop maps-links workbook (.xlsx)"}
        </div>
        <div className="mt-1 text-xs text-slate-500">
          Columns: facility_id · name · maps_link. Empty maps_link rows are skipped.
        </div>
      </div>

      <div className="flex flex-wrap justify-end gap-2">
        {mapsFile && (
          <button
            onClick={() => { setMapsFile(null); setMapsPreview(null); setMapsReport(null); }}
            className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            Clear
          </button>
        )}
        <button
          data-testid="maps-links-preview"
          onClick={previewMaps}
          disabled={!mapsFile || mapsPreviewing}
          className="inline-flex items-center gap-2 rounded-md border border-nmi-600 bg-white px-4 py-2 text-xs font-semibold text-nmi-700 hover:bg-nmi-50 disabled:opacity-50"
        >
          {mapsPreviewing ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} strokeWidth={2.5} />}
          Preview changes
        </button>
      </div>

      {mapsPreview && (
        <div className="mt-4 space-y-4 rounded-md border border-slate-200 bg-white p-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Preview</div>
            <div className="mt-2 grid grid-cols-2 gap-3 md:grid-cols-5">
              <Stat label="Rows in file" value={mapsPreview.counts.total_rows} />
              <Stat label="Matched" value={mapsPreview.counts.matched} tone="info" />
              <Stat label="Will change" value={mapsPreview.counts.will_change} tone="good" />
              <Stat label="Ambiguous" value={mapsPreview.counts.ambiguous} tone="warn" />
              <Stat label="Unmatched" value={mapsPreview.counts.unmatched} tone="warn" />
            </div>
          </div>

          {mapsPreview.counts.will_change > 0 && (
            <details className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs" open>
              <summary className="cursor-pointer font-semibold text-slate-800">
                Will change ({mapsPreview.counts.will_change})
              </summary>
              <div className="mt-2 max-h-64 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                      <th className="py-1 pr-2">Facility</th>
                      <th className="py-1 pr-2">Old link</th>
                      <th className="py-1"></th>
                      <th className="py-1">New link</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mapsPreview.will_change.slice(0, 200).map((r) => (
                      <tr key={r.facility_id} className="border-t border-slate-200 align-top">
                        <td className="py-1 pr-2">
                          <div className="font-semibold">{r.name}</div>
                          <div className="font-mono text-[10px] text-slate-500">{r.facility_id} · {r.match_by}</div>
                        </td>
                        <td className="py-1 pr-2 max-w-[220px] truncate text-red-800 line-through">{r.old_maps_link || <span className="italic text-slate-400">empty</span>}</td>
                        <td className="py-1 px-1"><ArrowRight size={11} className="text-slate-400" /></td>
                        <td className="py-1 max-w-[260px] truncate text-emerald-800">{r.new_maps_link}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          {mapsPreview.counts.ambiguous > 0 && (
            <details className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs">
              <summary className="cursor-pointer font-semibold text-amber-900">
                Ambiguous name matches — skipped ({mapsPreview.counts.ambiguous})
              </summary>
              <ul className="mt-2 space-y-1 pl-4 list-disc">
                {mapsPreview.ambiguous.slice(0, 50).map((r) => (
                  <li key={`amb-${r.row_num}`}>
                    Row {r.row_num} <b>"{r.name}"</b> matched {r.matches.length}+ facilities. Please add the facility_id column.
                  </li>
                ))}
              </ul>
            </details>
          )}

          {mapsPreview.counts.unmatched > 0 && (
            <details className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs">
              <summary className="cursor-pointer font-semibold">
                Unmatched rows — skipped ({mapsPreview.counts.unmatched})
              </summary>
              <ul className="mt-2 space-y-1 pl-4 list-disc">
                {mapsPreview.unmatched.slice(0, 50).map((r) => (
                  <li key={`unm-${r.row_num}`}>Row {r.row_num}: {r.facility_id || r.name}</li>
                ))}
              </ul>
            </details>
          )}

          <div className="flex justify-end">
            <button
              data-testid="maps-links-apply"
              onClick={() => {
                if (mapsPreview.counts.will_change === 0) {
                  toast.info("No changes to apply.");
                  return;
                }
                if (!window.confirm(`Update maps_link on ${mapsPreview.counts.will_change} facilities?`)) return;
                applyMaps();
              }}
              disabled={mapsApplying || mapsPreview.counts.will_change === 0}
              className="inline-flex items-center gap-2 rounded-md bg-nmi-700 px-4 py-2 text-xs font-semibold text-white hover:bg-nmi-800 disabled:opacity-50"
            >
              {mapsApplying ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} strokeWidth={2.5} />}
              Apply {mapsPreview.counts.will_change} updates
            </button>
          </div>
        </div>
      )}

      {mapsReport && (
        <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle2 size={14} /> <b>Maps links updated.</b>
          </div>
          <div>
            Updated {mapsReport.updated} · Unchanged {mapsReport.skipped_unchanged} ·
            Ambiguous {mapsReport.ambiguous} · Unmatched {mapsReport.unmatched} · Total rows {mapsReport.total_rows}
          </div>
        </div>
      )}
    </div>
  );
};
